import Phaser from "phaser";
import { Palette } from "./palette";

/**
 * Every non-tile sprite in the prototype is a runtime-generated texture - no
 * PNGs to manage while there's no art yet. During the art phase these keys get
 * replaced by loaded spritesheets in PreloadScene; the rest of the game keeps
 * referencing the same keys, so nothing else changes.
 */
export const Tex = {
  playerDan: "px_player_dan",
  playerLucas: "px_player_lucas",
  playerAndrew: "px_player_andrew",
  projectile: "px_projectile",
  box: "px_box",
  key: "px_key",
  door: "px_door",
  doorOpen: "px_door_open",
  switchOff: "px_switch_off",
  switchOn: "px_switch_on",
  plate: "px_plate",
  plateOn: "px_plate_on",
  bugSmall: "px_bug_small",
  bugBig: "px_bug_big",
  vault: "px_vault",
  elevator: "px_elevator",
  battery: "px_battery", // Lucas power crate (pushed onto a power pad to add machine power)
  machine: "px_machine", // Lucas due-diligence machine backdrop
  processPad: "px_process_pad", // Lucas process station mount zone (under the bike)
  processPadOn: "px_process_pad_on", // Lucas process station (running)
  bike: "px_bike", // Lucas exercise-bike station placeholder (real art swaps in later)
  // Real art (loaded in PreloadScene), not generated: sheepy is a 9-frame
  // spritesheet; the throwables are Dan's phone/email/Slack.
  sheepy: "sheepy",
  throwPhone: "throw-phone",
  throwEmail: "throw-email",
  throwSlack: "throw-slack",
  throwCode: "throw-code",
  throwClaude: "throw-claude",
  // Sci-fi menu chrome (loaded in PreloadScene): the START button (default +
  // hover) and the shared metal frame used behind menu cards.
  start: "start",
  startHover: "start-hover",
  hudBox: "hud-box",
  hudBoxHover: "hud-box-hover",
  sheepyHd: "sheepy-hd", // crisp single-frame sheepy for the level-select card badge (collected)
  sheepyOff: "sheepy-off", // greyed sheepy for the level-select card badge (not collected)
  lock: "lock", // padlock icon shown on locked level-select cards
  victory: "victory", // Mallorca ending celebration (512x292 spritesheet, 151 frames)
  regionEurOpen: "region-eur-open", // market map: Europe open (white glow), America locked
  regionEurHover: "region-eur-hover", // ... Europe hovered (gold glow), America locked
  regionUsaOpen: "region-usa-open", // market map: both open (America unlocked)
  regionUsaHover: "region-usa-hover", // ... America hovered (gold glow)
  regionUsaWon: "region-usa-won", // market map: America won (relay cleared)
  star: "star-on", // lit gold star (real art, loaded in PreloadScene)
  starEmpty: "star-off", // unlit bronze star (real art, loaded in PreloadScene)
  pixel: "px_pixel",
  money: "px_money",
  laptop: "px_laptop",
  vw: "px_vw",
  document: "px_document",
  feature: "px_feature",
  idea: "px_idea",
  bionic: "px_bionic",
  // Lucas roof rig real art (loaded in PreloadScene). battery/key above load real art
  // over their placeholder keys; these are the roof overlays and the cable.
  cable: "cable", // coiled cable collectible (found in the garage)
  bikeCable: "bike-cable", // the cable drawn connecting bike power to the bike once attached
  bikeTerminal: "bike-cable-terminal", // the "BIKE POWER" wall unit the cable plugs into
  bionicSign: "bionic-sign-light", // the lit BIONIC sign overlay that fills as he rides
  closedDoor: "closed-door", // upper door, locked/closed (over the open door in the base art)
  garageDoor: "garage-door", // garage shutter, locked/closed (over the open garage in the base art)
  openDoorMask: "open-door-mask", // foreground slice of the open upper door so Lucas walks behind it
  gameOver: "game-over",
  claudeMega: "claude-coin",
  regionConquered: "region-conquered",
  dataVaultDoor: "data-vault-door", // relay part 2: the open vault interior, overlaid over the closed door in the base art once unlocked
  batteryEmpty: "battery-empty", // per-slot empty-battery overlay; removed when a battery is loaded to reveal the full battery in the base art
  batteryInstallStart: "battery-installed-start", // one-shot power-up when a battery is seated (5x4 grid, 256px, 17 frames)
  batteryInstallLoop: "battery-installed-loop", // steady loop after the power-up (3x3 grid, 256px, 8 frames)
} as const;

export function collectibleTex(kind: string): string {
  switch (kind) {
    case "laptop":
      return Tex.laptop;
    case "vw":
      return Tex.vw;
    case "document":
      return Tex.document;
    case "feature":
      return Tex.feature;
    case "idea":
    case "lightbulb":
      return Tex.idea;
    case "bionic":
      return Tex.bionic;
    default:
      return Tex.money;
  }
}

/** Dan's throwable art by tier: 2 phone, 3 email, 4 Slack. Falls back to the
 *  generic projectile for the (unused) tier-0/1 case. */
export function throwableTex(tier: number): string {
  switch (tier) {
    case 2:
      return Tex.throwPhone;
    case 3:
      return Tex.throwEmail;
    case 4:
      return Tex.throwSlack;
    default:
      return Tex.projectile;
  }
}

/** The looping animation key for Sheepy; register it once per scene before use. */
export const SHEEPY_ANIM = "sheepy-bob";

/** Register Sheepy's 9-frame bob animation on a scene (idempotent). */
export function ensureSheepyAnim(scene: Phaser.Scene) {
  if (scene.anims.exists(SHEEPY_ANIM)) return;
  scene.anims.create({
    key: SHEEPY_ANIM,
    frames: scene.anims.generateFrameNumbers(Tex.sheepy, { start: 0, end: 8 }),
    frameRate: 12,
    repeat: -1,
  });
}

export function buildTextures(scene: Phaser.Scene) {
  const g = scene.add.graphics();

  const rect = (key: string, w: number, h: number, fill: number, stroke = 0x0e0f13) => {
    g.clear();
    g.fillStyle(fill, 1);
    g.fillRect(0, 0, w, h);
    g.lineStyle(2, stroke, 1);
    g.strokeRect(1, 1, w - 2, h - 2);
    g.generateTexture(key, w, h);
  };

  const circle = (key: string, r: number, fill: number, stroke = 0x0e0f13) => {
    g.clear();
    g.fillStyle(fill, 1);
    g.fillCircle(r, r, r);
    g.lineStyle(2, stroke, 1);
    g.strokeCircle(r, r, r - 1);
    g.generateTexture(key, r * 2, r * 2);
  };

  // players - distinct silhouette per character (all ~22x30)
  rect(Tex.playerDan, 22, 30, Palette.dan);
  rect(Tex.playerLucas, 22, 30, Palette.lucas);
  rect(Tex.playerAndrew, 22, 30, Palette.andrew);

  // collectibles are all real art now, loaded in PreloadScene under their Tex
  // keys (money, laptop, vw, document, feature, idea). Nothing to generate here.

  // misc gameplay objects
  circle(Tex.projectile, 6, Palette.projectile);
  rect(Tex.box, 30, 60, Palette.box); // pushable crate, ~2 tiles tall (a real step)
  // Tex.key / Tex.battery are real art loaded in PreloadScene. Do NOT generate
  // placeholders here: BootScene runs first, and a pre-existing texture key makes
  // PreloadScene's load.image a no-op (Phaser skips keys already in use).
  rect(Tex.door, 28, 44, Palette.door);
  rect(Tex.doorOpen, 28, 44, Palette.doorOpen);
  rect(Tex.switchOff, 20, 20, Palette.switchOff);
  rect(Tex.switchOn, 20, 20, Palette.switchOn);
  rect(Tex.plate, 30, 10, 0x8a5a5a); // pressure plate (up)
  rect(Tex.plateOn, 30, 8, 0x55cc77); // pressure plate (pressed)
  circle(Tex.bugSmall, 11, Palette.bug);
  circle(Tex.bugBig, 18, Palette.bug);
  rect(Tex.vault, 30, 40, 0x6b6f7a);
  rect(Tex.elevator, 96, 16, 0x8a8f9c); // moving platform (3 tiles wide)
  rect(Tex.machine, 160, 160, 0x30343d); // Lucas machine backdrop (scaled to its object)
  rect(Tex.processPad, 44, 12, 0x4f6fd6); // Lucas process station (idle)
  rect(Tex.processPadOn, 44, 12, 0x8fd0ff); // Lucas process station (running)
  rect(Tex.bike, 40, 46, 0x5b6b8c); // Lucas exercise-bike placeholder (real Lucas+bike sheet swaps in later)

  // HUD bits
  g.clear();
  g.fillStyle(0xffffff, 1);
  g.fillRect(0, 0, 1, 1);
  g.generateTexture(Tex.pixel, 1, 1);

  // Stars (Tex.star / Tex.starEmpty) are real art loaded in PreloadScene; nothing to
  // generate here. Note the Phaser gotcha: generating a texture under those keys here
  // would make PreloadScene's load.image skip the real files.

  g.destroy();
}

